<template>
  <div v-resize="onResize">
    <v-card 
      class="ma-2"
      flat>
      <v-card-title class="pa-0">
        <v-btn
          :color="isGenomicCheck?'success':'tertiary'"
          class="white--text mx-0 my-2"
          @click="navigateBack()">
          <v-icon large>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="flex-grow-1"></div>
        <div :class="breakpoint === 'xs' ? 'text-h6' : 'text-h5'">{{ groupName }}</div>
        <v-spacer />
        <div v-if="backupFileName" class="mr-2 d-flex flex-wrap">
            <v-icon large color="green">mdi-book</v-icon>
            <span style="line-height : 15px" class="py-1 text-caption">Backup <br /> Enabled</span>
        </div>
        <div v-else-if="isBackupCapabilityAvailable" class="d-flex flex-wrap mr-2">
            <v-icon large color="red">mdi-book-cancel</v-icon>
            <span style="line-height : 15px" class="pl-1 py-1 text-caption">Backup<br />Disabled</span>
        </div>
      </v-card-title>
      <v-card-text class="px-1">
        <v-form ref="form" v-model="validForm">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="tube"
                ref="tubeRef"
                label="Tube"
                clearable
                @keypress="maskAlphaNumeric($event)"
                @keydown.enter="valueEntered()"
                @keyup="tube = maskTube(tube)"
                @focus="$event.target.select(); clearSearchResults();"
                :rules="[rules.required, v => isTubeValidRule(v), v => isTubeExistingRule(v)]"
              />
              <v-text-field
                v-model="wingband"
                ref="wingbandRef"
                label="Wingband"
                clearable
                @keypress="maskAlphaNumeric($event)"
                @keydown.enter="valueEntered()"
                @keyup="wingband = maskWingband(wingband)"
                @focus="$event.target.select(); clearSearchResults();"
                :rules="[rules.required, v => isWingbandValidRule(v), v => isWingbandExistingRule(v)]"
              />
              <v-text-field
                v-model="confirmTube"
                ref="confirmTubeRef"
                label="Confirm Tube"
                clearable
                @keypress="maskAlphaNumeric($event)"
                @keydown.enter="valueEntered()"
                @keyup="confirmTube = maskTube(confirmTube)"
                @focus="$event.target.select(); clearSearchResults();"
                :rules="[rules.required, v => isTubeValidRule(v), v => isTubeExistingRule(v)]"
              />
            </v-col>
            <v-col cols="6" class="pr-0">
              <v-btn
                fab
                :small="breakpoint === 'xs'"
                :class="breakpoint === 'xs' ? 'white--text mt-3 mb-1': 'white--text'"
                color="primary"
                :disabled="addSampleDisabled"
                @click="valueEntered()">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
              <v-btn
                :class="breakpoint === 'xs' ? 'white--text mt-3 mb-1 ml-2': 'white--text ml-2'"
                fab
                :small="breakpoint === 'xs'"
                color="primary"
                @click="resetFields()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-btn v-if="isGenomicCheck"
                :class="breakpoint === 'xs' ? 'mt-3 mb-1 ml-2': 'ml-2'"
                fab
                :small="breakpoint === 'xs'"
                color="success"
                @click="showCheckerDialog = true">
                <v-icon>mdi-check-decagram-outline</v-icon>
              </v-btn>
              <div style="padding-top:15px;">
                <v-text-field class="mr-4"
                  v-model="searchValue"
                  label="Search"
                  clearable
                  @input="searchSamples()"
                />
              </div>
              <div style="padding-top:18px;">
                <div class="text-subtitle-1">
                  <span>Sample Count:</span>
                  <span class="pl-1 font-weight-bold">{{ currentBleedSamples.length }}</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>

        <v-card class="mt-0">
          <!-- <v-simple-table
            :fixed-header="true"
            :height="tableHeight"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center">Tube</th>
                  <th class="text-center">Wingband</th>
                  <th class="text-center">Date</th>
                  <th class="text-center">Delete</th>
                </tr>
              </thead>  
              <tbody>
                <tr v-for="item in filteredBleedSamples" :key="item.wingband">
                  <td class="pa-0 text-center">{{ item.tube }}</td>
                  <td class="pa-0 text-center">{{ item.wingband }}</td>
                  <td class="pa-0 text-center" nowrap>{{ formatDate(item.date) }}</td>
                  <td class="pa-0 text-center">
                    <v-btn
                      fab small
                      color="secondary" 
                      class="my-2 mx-0" 
                      @click="confirmDeleteSample(item)">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table> -->

          <v-data-table
              :height="tableHeight"
              mobile-breakpoint="300"
              disable-sort
              dense
              :headers="tblHeaders"
              :items="filteredBleedSamples"
              class="elevation-1 pt-1 cardbackground"
              :search="searchValue"
              :footer-props="{
                itemsPerPageOptions: breakpoint === 'xs'?[5,10,25]: [10,25,50],
                'xxdisable-items-per-page':  breakpoint === 'xs'? true: false,
                itemsPerPageText : breakpoint === 'xs'? 'rows': 'rows per page'
              }"
          >
            <template v-slot:[`item.tube`]="{ item }">
              <div class="ma-0 pa-0 caption">{{ item.tube }}</div>
            </template>
            <template v-slot:[`item.wingband`]="{ item }">
              <pre class="ma-0 pa-0 caption">{{ item.wingband }}</pre>
            </template>
            <template v-slot:[`item.date`]="{ item }">
              <pre class="ma-0 pa-0 caption">{{ formatDate(item.date) }}</pre>
            </template>
            <template v-slot:[`item.delete`]="{ item }">
              <v-btn
                fab small
                color="secondary" 
                class="my-1 mx-0" 
                @click="confirmDeleteSample(item)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showDeleteDialog" persistent max-width="350">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Confirm Delete</v-card-title>
        <v-card-text>Is it okay to delete <br />
          Tube: <b>{{tubeToDelete}}</b>, Wingband: <b>{{ wingbandToDelete }}</b>?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" class="white--text" @click="deleteSample()">Yes</v-btn>
          <v-btn color="secondary" @click="showDeleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

     <v-dialog v-model="showCheckerDialog" persistent max-width="310">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Check Wingband Exists</v-card-title>
        <v-card-text>
          <div class="d-flex">
            <v-text-field
              v-model="checkWingband"
              ref="checkWingbandRef"
              label="Wingband"
              clearable
              @keypress="maskAlphaNumeric($event)"
              @keydown.enter="checkButtonEnabled ? checkWingbandExists(checkWingband): null"
              @keyup="checkWingband = maskWingband(checkWingband)"         
            />
            <v-btn :disabled="!checkButtonEnabled" fab color="success" class="ml-8 white--text" @click="checkWingbandExists(checkWingband)">
              <v-icon>mdi-check-decagram-outline</v-icon>
            </v-btn>
            <v-spacer />
          </div>
        </v-card-text>
        <v-card-actions class="pt-0 mr-2">
          <v-spacer />
          <v-btn color="secondary" @click="showCheckerDialog = false; checkWingband = null">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>  
</template>
<script>
import OfflineStorage from '../offlineStorage'
import { mapState } from 'vuex'
import { format } from 'date-fns'
import fileSysApi from '@/api/fileSysApi'

export default {
  data() {
    return {
      backupFileName: null,
      checkWingband: null,
      confirmTube: null,
      currentBleedSamples: [],
      filteredBleedSamples: [],
      isCheckerDialogVisible: false,
      genomicBleedGroups: null,
      rules: [],
      searchValue: '',
      showDeleteDialog: false,
      tube: null,
      tubeToDelete: null,
      archivedGenomicBleedGroups: null,
      windowSize: {x: 0,y: 0},
      wingband: null,
      wingbandToDelete: null,
      validForm: true
    }
  },

  async mounted () {
    this.onResize();
    this.$refs.tubeRef.focus();
    if (this.isGenomicCheck) {
      this.showCheckerDialog = true;
    }

    try {
      var permission = await fileSysApi.getDirPermissions(this.settings.genomicBackupDir);
      if (permission == 'granted') {
        this.backupFileName = await fileSysApi.getNewFileName(this.settings.genomicBackupDir, this.groupName);
      }
    }
    catch(error) {
      this.$root.showNoticeError(`Unable to get Backup File Handle: ${error.message}`);
    }
  },

  async created() {
    try {
      this.rules = { ...this.$root.validationRules };
      
      this.genomicBleedGroups = await OfflineStorage.getItem('genomicBleedGroups');
      
      this.archivedGenomicBleedGroups = await OfflineStorage.getItem('archivedGenomicBleedGroups');
      if (this.archivedGenomicBleedGroups == null) this.archivedGenomicBleedGroups = [];

      const genomicBleedGroup = this.genomicBleedGroups.find(x => x.groupName === this.groupName);
      this.currentBleedSamples = genomicBleedGroup.samples;
      this.filteredBleedSamples = this.currentBleedSamples;
    } catch(error) {
      this.$root.showNoticeError(error.message);
    }
  },

  computed: {
    ...mapState(['settings', 'isOnline']),
    addSampleDisabled() {
      const disabled = !this.validForm;
      return disabled;
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    checkButtonEnabled() {
       if (!this.checkWingband) return false;
       else if (this.isWingbandValidRule(this.checkWingband) !== true) return false;
       else return true;
    },
    groupName() {
      return this.$route.params.groupName;
    },
    isBackupCapabilityAvailable() {
      return fileSysApi.isAvailable;
    },
    isGenomicCheck() {
      return this.$route.params.check;
    },
    showCheckerDialog: {
      get() { return this.isCheckerDialogVisible; },
      set(value) {
        this.isCheckerDialogVisible = value;
        if (value) {
          setTimeout(() => { this.$refs.checkWingbandRef.focus(); }, 100);
        } 
      }
    },
    tblHeaders() {
      const tableCols = [
        { text: 'Tube', value: 'tube', align: 'center', sortable: false},
        { text: 'Wingband', value: 'wingband', align: 'center' },
        { text: 'Date', value: 'date', align: 'center' },
        { text: 'Delete', value: 'delete', align: 'center'}
      ];
      return tableCols;
    },
    tableHeight() {
      let windowSize = this.windowSize.y - 422;

      if (this.$vuetify.breakpoint.name == 'xs' || this.$vuetify.breakpoint.name === 'sm') {
        windowSize += 4; // v-app-bar shorter for xs and sm
      }

      if (windowSize < 250) windowSize = 250;

      return windowSize;
    }
  },

  methods: {
    async addSample() {
      if (this.addSampleDisabled) return;

      try {
        const now = format(new Date(), 'yyyy-MM-dd hh:mm:ss aa');

        const sample = {
          tube: this.tube,
          wingband: this.wingband,
          date: now
        };

        this.currentBleedSamples.unshift(sample); //add to beginning

        await this.saveData(); 
        this.$root.showNoticeSuccess(`Sample added successfully.`);

        this.tube = null;
        this.confirmTube = null;
        this.wingband = null;

        this.$refs.form.resetValidation();
        this.$refs.tubeRef.focus();
        this.makeSuccessSound();
        if (this.isGenomicCheck) {
          this.showCheckerDialog = true;
        }
      } catch(error) {
        this.$root.showNoticeError(error.message);
      }
    },
    checkWingbandExists(wb) {
      let el = this.$refs.checkWingbandRef.$el.querySelector('input');
      el.select();

      let result = this.isWingbandExistingRule(wb);
      if (result === true) { // wingband does not exist.
        setTimeout(() => {
          this.showCheckerDialog = false;
          this.checkWingband = null;
          this.resetFields();
          this.$root.showNoticeError(`${wb} does not exist. Please create sample.`,this.resetFields);
        },100);
      }
      else {
        this.$root.showNoticeSuccess(`${wb} exists.`);
        this.makeSuccessSound();
      }
    },
    clearSearchResults() {
      this.$nextTick(() => {
        if (this.searchValue) {
          this.searchValue = '';
          this.searchSamples();
        }
      });
    },
    confirmDeleteSample(item) {
      this.$refs.form.resetValidation();
      this.wingbandToDelete = item.wingband;
      this.tubeToDelete = item.tube;
      this.showDeleteDialog = true;
    },
    async deleteSample() {
      try {
        const index = this.currentBleedSamples.findIndex(x => x.wingband === this.wingbandToDelete);
        this.currentBleedSamples.splice(index, 1);

        await this.saveData();
        this.$root.showNoticeSuccess(`Tube: ${this.tubeToDelete} Wingband: ${this.wingbandToDelete} deleted successfully`);

        this.wingbandToDelete = null;
        this.showDeleteDialog = false;

        this.$refs.form.resetValidation();
        this.searchSamples(); // force samples to refresh
      } catch(error) {
        this.$root.showNoticeError(error.message);
      }
    },
    focusConfirmTube() {
      if (this.$refs.form == null || this.$refs.form.resetValidation == null) return;
      this.clearSearchResults();
      document.activeElement.blur();
      this.$refs.confirmTubeRef.focus();
    },
    focusTube() {
      if (this.$refs.form == null || this.$refs.form.resetValidation == null) return;
      this.clearSearchResults();
      document.activeElement.blur();
      this.$refs.tubeRef.focus();
    },
    focusWingband() {
      if (this.$refs.form == null || this.$refs.form.resetValidation == null) return;
      this.clearSearchResults();
      document.activeElement.blur();
      this.$refs.wingbandRef.focus();
    },
    formatDate(dt) {
      if (this.breakpoint === 'xs'){
        return dt.split(' ')[0];
      }
      return dt;
    },
    isTubeExistingRule(v) {
      if (!v) return true;

      if (this.currentBleedSamples.find(s => s.tube === v)) {
        return `Tube ${v} already used in this bleed group.`;
      }
      var group = this.genomicBleedGroups.find(s => s.samples.find(w => w.tube == v));
      if (group != null) {
        return `Tube ${v} already used in ${group.groupName}.`;
      }
      group = this.archivedGenomicBleedGroups.find(s => s.samples.find(w => w.tube == v));
      if (group != null) {
        return `Tube ${v} already used in ${group.groupName}.`;
      }

      return true;
    },
    isTubeValidRule(v) 
    {
      if (!v) return true;

      const farmName = this.settings.currentFarm || '';
      const isHVFarm = farmName.includes('HV');

      if (isHVFarm) {
        // HV farms: allow AA12345678, 1234567, 1234567890, 12345678901, or 123456789012 (7 or 10-12 digits)
        if (/^[A-Z]{2}\d{8}$/.test(v) == false && 
            /^\d{7}$/.test(v) == false &&
            /^\d{10}$/.test(v) == false && 
            /^\d{11}$/.test(v) == false &&
            /^\d{12}$/.test(v) == false) {
          return "Invalid Tube Format. Valid formats are AA12345678, 1234567, 1234567890, 12345678901, or 123456789012 (7 or 10-12 digits).";
        }
      } else {
        // Non-HV farms: existing validation (AA12345678 or 1234567890)
        if (/^[A-Z]{2}\d{8}$/.test(v) == false && /^\d{10}$/.test(v) == false) {
          return "Invalid Tube Format. Valid formats are AA12345678 or 1234567890.";
        }
      }
      if (this.tube && this.confirmTube && this.tube !== this.confirmTube) 
        return "Tube mismatch.";

      return true;
    },
    isWingbandExistingRule(v) {
      if (!v) return true;

      if (this.currentBleedSamples.find(s => s.wingband === v)) {
        return `Wingband ${v} already used in this bleed group.` ;
      }

      let group = this.genomicBleedGroups.find(s => s.samples.find(w => w.wingband == v));
      if (group != null) {
        return `Wingband ${v} already used in ${group.groupName}.`;
      }

      group = this.archivedGenomicBleedGroups.find(s => s.samples.find(w => w.wingband == v));
      if (group != null) {
        return `Wingband ${v} already used in ${group.groupName}.`;
      }

      return true;
    },
    isWingbandValidRule(v) {
      
      if (!v) return true;

      const farmName = this.settings.currentFarm || '';
      const isHVFarm = farmName.includes('HV');

      if (isHVFarm) {
        // HV Farms: allow AB1234, ABC1234, 123, 1234, 12345, or 123456
        if (/^[A-Z]{2}\d{4}$/.test(v) === false && /^[A-Z]{3}\d{4}$/.test(v) === false && /^\d{3,6}$/.test(v) === false) {
          return 'Invalid Wingband. Valid formats are AB1234, ABC1234, 123, 1234, 12345 or 123456.';
        }
        // Non-HV farms: existing validation (ABC1234 or 12345/123456)
      } else {
        if (/^[A-Z]{3}\d{4}$/.test(v) === false && /^\d{5}(?:\d{1})?$/.test(v) === false) {
          return 'Invalid Wingband. Valid formats are ABC1234, 12345 or 123456.';
        }
      }
      return true;
    },
    makeSuccessSound() {
      if (this.settings.isSuccessSoundEnabled) {
        const audio = new Audio('sound/success-chime.mp3');
        audio.play();
      }
    },
    maskAlphaNumeric(e) {
      let char = String.fromCharCode(e.keyCode);
      if(/^[A-Za-z0-9]+$/.test(char)) 
        return char.toUpperCase();
      else
        e.preventDefault(); // If not match, don't add to input text
    },
    maskTube(tube) {
      if (tube == null) {
          return;
      }
      return tube.toUpperCase();
      //let char = tube.charAt(0);
      //let str = "";
      // let i = 0;

      // // Check for AA######## format
      // if (/^[A-Za-z]+$/.test(char)) {
      //     str = char;
      //     for (i = 1; i < tube.length && i<2; i++) {
      //         char = tube.charAt(i);
      //         if (/^[A-Za-z]+$/.test(char)) 
      //         str += char;
      //     }
      //     for (i = 2; i < tube.length && i<10; i++) {
      //         char = tube.charAt(i);
      //         if (/^[0-9]+$/.test(char))
      //         str += char;
      //     }
      // }
      // else { /// Must be ##########
      //     for (i = 0; i < tube.length && i<10; i++) {
      //         char = tube.charAt(i);
      //         if (/^[0-9]+$/.test(char)) 
      //         str += char;
      //     }
      // }

      //return str.toUpperCase();
    },
    maskWingband(wingband) {
      if (wingband == null) {
          return;
      }
      return wingband.toUpperCase();

      // let char = wingband.charAt(0);
      // let str = char;
      // let i = 0;

      // // Check for AAA#### format
      // if (/^[A-Za-z]+$/.test(char)) { //isAlpha
      //     for (i = 1; i < wingband.length && i<3; i++) {
      //         char = wingband.charAt(i);
      //         if (/^[A-Za-z]+$/.test(char)) str += char;
      //     }

      //     if (str.length < 3) return str.toUpperCase();

      //     for (i = 3; i < wingband.length && i<7; i++) {
      //         char = wingband.charAt(i);
      //         if (/^[0-9]+$/.test(char)) str += char;
      //     }
      // }
      // else { /// Must be ###### format
      //     for (i = 1; i < wingband.length && i<6; i++) {
      //         char = wingband.charAt(i);
      //         if (/^[0-9]+$/.test(char)) str += char;
      //     }
      // }

      //return str.toUpperCase();
    },
    navigateBack() {
      if (this.isGenomicCheck)
        this.$router.replace({ name: 'Genomic Check', params: {check: true}});
      else
        this.$router.replace({ name: 'Genomic Bleed'});
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight }
    },
    resetFields() {
      // Return if this was called from error notice after navigating to another page.
      if (this.$refs.form == null || this.$refs.form.resetValidation == null) return;

      this.searchValue = '';
      this.wingband = null;
      this.tube = null;
      this.confirmTube = null;
      setTimeout(() => { // needed for timing issue after closing error notice.
        this.$refs.form.resetValidation();
        this.$refs.tubeRef.focus();
      }, 100);
    },
    async saveData() {
      const genomicBleedGroup = this.genomicBleedGroups.find(x => x.groupName === this.groupName);
      genomicBleedGroup.samples = this.currentBleedSamples;
      await OfflineStorage.setItem('genomicBleedGroups', this.genomicBleedGroups);

      try {
        if (this.backupFileName) {
          var header = "Operator,Farm,Line,Hatch,MG,Chicken_barcode,Tube_barcode,Date," +
                "Blank_1,Blank_2,Blank_3,Blank_4,Blank_5,Blank_6,Blank_7,Blank_8,Blank_9,Blank_10";
          var rows = this.getCsvDataRows(genomicBleedGroup);
          await fileSysApi.saveToFile(this.settings.genomicBackupDir, this.backupFileName, header, rows);
        }
      }
      catch (error) {
        throw new Error (`Backup Failed. ${error.message}`)
      }
    },
    getCsvDataRows(bleedGroup) {
      var operator = bleedGroup.operator;
      var farm = bleedGroup.farm;
      var line = bleedGroup.line;
      var minigeneration = bleedGroup.minigeneration;
      var disp = bleedGroup.disp;
      var trex = bleedGroup.trex;
      var notes = bleedGroup.notes;
      var gender = bleedGroup.gender;

      // Get Hatch and Group
      var hatch = "0";
      var group = `${bleedGroup.farm}_${bleedGroup.line}_MG${bleedGroup.minigeneration}`;
      var hatchGrpStr = "";
      if (bleedGroup.hatch != null && bleedGroup.hatch != "0")
      {
          hatch = bleedGroup.hatch;
          hatchGrpStr = `_H${bleedGroup.hatch}`;
      }
      group += `${hatchGrpStr}_RUN_${bleedGroup.disp}`;

      var csvLines = [];
      bleedGroup.samples.forEach(s => {
        var wingband = s.wingband;
        var tube = s.tube;
        var date = s.date;

         var csvLine = `${operator},${farm},${line},${hatch},${minigeneration},` +
                `${wingband},${tube},${date},${disp},${gender},` +
                `${trex},${notes},${group},,,,,`;
        csvLines.push(csvLine);
      });

      return csvLines;
    },
    searchSamples() {
        if (this.searchValue) {
          const searchVal = this.searchValue.toUpperCase();
          this.filteredBleedSamples = this.currentBleedSamples
          .filter(s => s.wingband.includes(searchVal) || s.tube.includes(searchVal));
        }
        else {
          this.filteredBleedSamples = this.currentBleedSamples;
        }
    },
    valueEntered() { 
     // Check Tube, Wingband, and Confirm Tube are valid format.
     let msg = this.isTubeValidRule(this.tube);
      if (msg !== true) {
        this.$root.showNoticeError(msg, this.focusTube);
        this.focusTube();
        return;
      }
      msg = this.isWingbandValidRule(this.wingband);
      if (msg !== true) {
        this.$root.showNoticeError(msg, this.focusWingband);
        this.focusWingband();
        return;
      }
      msg = this.isTubeValidRule(this.confirmTube);
      if (msg !== true) {
        this.$root.showNoticeError(msg, this.focusConfirmTube);
        this.focusConfirmTube();
        return;
      }

      // Check Tube, Wingband, and Confirm tube are unique
      msg = this.isTubeExistingRule(this.tube);
      if (msg !== true) {
        this.$root.showNoticeError(msg, this.focusTube);
        this.focusTube();
        return;
      }
      msg = this.isWingbandExistingRule(this.wingband); 
      if (msg !== true) {
        this.$root.showNoticeError(msg, this.focusWingband);
        this.focusWingband();
        return;
      }
      msg = this.isTubeExistingRule(this.confirmTube);
      if (msg !== true) {
         this.$root.showNoticeError(msg, this.focusConfirmTube);
        this.focusConfirmTube();
        return;
      }

      // If Add sample disabled then Set focus to appropriate field.
      if (this.addSampleDisabled) {
        if (!this.tube) this.$refs.tubeRef.focus();
        else if (!this.wingband)  this.$refs.wingbandRef.focus();
        else if (!this.confirmTube) this.$refs.confirmTubeRef.focus();
        return;
      }
      this.addSample();
    }
  }
}
</script>

<style>
.text-center { /* shrink table padding */
  padding: 0px !important;
  font-size: 13px !important;
}

/* .v-select__slot input { color: var(--lineTextColor) !important} */
.v-select__selection { color: var(--lineTextColor) !important}
.v-data-footer {flex-wrap: nowrap; padding: 0 0 0 0}
</style>